import {
  Tower,
  TowerStats,
  TowerTier,
  Team,
  Lane,
  TOWER_RANGE_GRID,
} from './simulationTypes';

/* ══════════════════ Tower stats by tier ══════════════════ */

const TOWER_STATS: Record<TowerTier, TowerStats> = {
  outer: {
    maxHealth: 9000,
    attackDamage: 152,
    attackSpeed: 0.83,
    attackRange: TOWER_RANGE_GRID,
    armor: 60,
    magicResist: 60,
  },
  inner: {
    maxHealth: 5000,
    attackDamage: 170,
    attackSpeed: 0.83,
    attackRange: TOWER_RANGE_GRID,
    armor: 60,
    magicResist: 60,
  },
  inhib: {
    maxHealth: 4750,
    attackDamage: 170,
    attackSpeed: 0.83,
    attackRange: TOWER_RANGE_GRID,
    armor: 60,
    magicResist: 60,
  },
  nexus: {
    maxHealth: 3500,
    attackDamage: 150,
    attackSpeed: 0.83,
    attackRange: TOWER_RANGE_GRID,
    armor: 60,
    magicResist: 60,
  },
};

/* ══════════════════ Tower definitions ══════════════════
 *
 * Positions extracted from the walkability-grid2.json turret layer.
 * Grid coords are 0–511 on the 512×512 map.
 *
 * Blue base = bottom-left (low x, high y)
 * Red  base = top-right   (high x, low y)
 *
 * Top lane: left edge (blue side) → top edge (red side)
 * Mid lane: diagonal from bottom-left → top-right
 * Bot lane: bottom edge (blue side) → right edge (red side)
 *
 * Per team, tier order from base outward:
 *   nexus (×2) → inhib (T3) → inner (T2) → outer (T1)
 * ══════════════════════════════════════════════════════ */

interface TowerDef {
  id: string;
  team: Team;
  lane: Lane | 'base';
  tier: TowerTier;
  gx: number;
  gy: number;
}

const TOWER_DEFS: TowerDef[] = [
  /* ── Blue team ── */

  // Top lane (left edge, ordered base → outer)
  { id: 'blue-top-inhib',  team: 'blue', lane: 'top', tier: 'inhib', gx: 43,  gy: 363 },
  { id: 'blue-top-inner',  team: 'blue', lane: 'top', tier: 'inner', gx: 55,  gy: 281 },
  { id: 'blue-top-outer',  team: 'blue', lane: 'top', tier: 'outer', gx: 37,  gy: 155 },

  // Mid lane (diagonal, ordered base → outer)
  { id: 'blue-mid-inhib',  team: 'blue', lane: 'mid', tier: 'inhib', gx: 128, gy: 384 },
  { id: 'blue-mid-inner',  team: 'blue', lane: 'mid', tier: 'inner', gx: 175, gy: 345 },
  { id: 'blue-mid-outer',  team: 'blue', lane: 'mid', tier: 'outer', gx: 203, gy: 292 },

  // Bot lane (bottom edge, ordered base → outer)
  { id: 'blue-bot-inhib',  team: 'blue', lane: 'bot', tier: 'inhib', gx: 149, gy: 467 },
  { id: 'blue-bot-inner',  team: 'blue', lane: 'bot', tier: 'inner', gx: 238, gy: 460 },
  { id: 'blue-bot-outer',  team: 'blue', lane: 'bot', tier: 'outer', gx: 361, gy: 475 },

  // Nexus turrets (in base)
  { id: 'blue-nexus-1',    team: 'blue', lane: 'base', tier: 'nexus', gx: 63,  gy: 432 },
  { id: 'blue-nexus-2',    team: 'blue', lane: 'base', tier: 'nexus', gx: 78,  gy: 448 },

  /* ── Red team ── */

  // Top lane (top edge, ordered base → outer)
  { id: 'red-top-inhib',   team: 'red', lane: 'top', tier: 'inhib', gx: 360, gy: 45  },
  { id: 'red-top-inner',   team: 'red', lane: 'top', tier: 'inner', gx: 273, gy: 53  },
  { id: 'red-top-outer',   team: 'red', lane: 'top', tier: 'outer', gx: 151, gy: 37  },

  // Mid lane (diagonal, ordered base → outer)
  { id: 'red-mid-inhib',   team: 'red', lane: 'mid', tier: 'inhib', gx: 382, gy: 128 },
  { id: 'red-mid-inner',   team: 'red', lane: 'mid', tier: 'inner', gx: 336, gy: 165 },
  { id: 'red-mid-outer',   team: 'red', lane: 'mid', tier: 'outer', gx: 308, gy: 220 },

  // Bot lane (right edge, ordered base → outer)
  { id: 'red-bot-inhib',   team: 'red', lane: 'bot', tier: 'inhib', gx: 467, gy: 150 },
  { id: 'red-bot-inner',   team: 'red', lane: 'bot', tier: 'inner', gx: 456, gy: 229 },
  { id: 'red-bot-outer',   team: 'red', lane: 'bot', tier: 'outer', gx: 475, gy: 356 },

  // Nexus turrets (in base)
  { id: 'red-nexus-1',     team: 'red', lane: 'base', tier: 'nexus', gx: 432, gy: 64  },
  { id: 'red-nexus-2',     team: 'red', lane: 'base', tier: 'nexus', gx: 447, gy: 80  },
];

/* ══════════════════ Create tower instances ══════════════════ */

export function createTowers(): Tower[] {
  return TOWER_DEFS.map((def) => {
    const stats = { ...TOWER_STATS[def.tier] };
    return {
      id: def.id,
      team: def.team,
      lane: def.lane,
      tier: def.tier,
      gx: def.gx,
      gy: def.gy,
      health: stats.maxHealth,
      maxHealth: stats.maxHealth,
      stats,
      targetId: null,
      attackCooldown: 0,
      alive: true,
    };
  });
}

/**
 * For a given lane + team, return the tower tiers in the order
 * minions should encounter them (outer first, then inner, then inhib).
 * Nexus towers are only reachable after all three inhib towers fall
 * (simplified: we just let minions attack them if they walk there).
 */
export const TIER_ORDER: TowerTier[] = ['outer', 'inner', 'inhib', 'nexus'];
